import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Upload, Loader2, Play, Image as ImageIcon, X, RefreshCw, Clock, Film, ChevronDown, Check, Maximize2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

async function pollTask(taskId, token) {
  const maxPolls = 600;
  let notFoundCount = 0;
  for (let i = 0; i < maxPolls; i++) {
    await new Promise(r => setTimeout(r, 3000));
    try {
      const { data } = await axios.get(`${API}/api/recipes/seedance-direct/task/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (data.status === 'done') return data;
      if (data.status === 'failed') throw new Error(data.error || 'Generation failed');
      notFoundCount = 0;
    } catch (err) {
      if (err.response?.status === 404) {
        notFoundCount++;
        if (notFoundCount > 30) throw new Error('Task lost');
      } else if (err.message && !err.response) {
        throw err;
      }
    }
  }
  throw new Error('Generation timed out');
}

export default function SeedanceDirectInputs({ token, onVideoStateChange, restoredData }) {
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [imageOrientation, setImageOrientation] = useState('landscape'); // 'landscape' or 'portrait'

  const [prompt, setPrompt] = useState('');
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [duration, setDuration] = useState(5);

  const [generating, setGenerating] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);

  const [runs, setRuns] = useState([]);
  const [runsOpen, setRunsOpen] = useState(true);
  const [lightbox, setLightbox] = useState(null);

  const fileInputRef = useRef(null);

  // Load runs on mount
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/api/recipes/seedance-direct/runs`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(({ data }) => setRuns(data.runs || [])).catch(() => {});
  }, [token]);

  // Restore from recent run
  useEffect(() => {
    if (!restoredData) return;
    if (restoredData.prompt) setPrompt(restoredData.prompt);
    if (restoredData.aspect_ratio) setAspectRatio(restoredData.aspect_ratio);
    if (restoredData.duration) setDuration(restoredData.duration);
    if (restoredData.image_url) {
      setImageUrl(restoredData.image_url);
      setImagePreview(restoredData.image_url);
      const img = new Image();
      img.onload = () => setImageOrientation(img.height > img.width ? 'portrait' : 'landscape');
      img.src = restoredData.image_url;
    }
    if (restoredData.video_url) {
      setVideoUrl(restoredData.video_url);
      onVideoStateChange?.(false, restoredData.video_url);
    }
  }, [restoredData]);

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    const objUrl = URL.createObjectURL(file);
    setImagePreview(objUrl);
    setImageUrl(null);
    // Detect orientation and auto-set aspect ratio
    const img = new Image();
    img.onload = () => {
      const isPortrait = img.height > img.width;
      setImageOrientation(isPortrait ? 'portrait' : 'landscape');
      setAspectRatio(isPortrait ? '9:16' : '16:9');
    };
    img.src = objUrl;
  };

  const uploadImage = async () => {
    if (!imageFile) return imageUrl;
    if (imageUrl) return imageUrl;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', imageFile);
      const { data } = await axios.post(`${API}/api/recipes/seedance-direct/upload-image`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        timeout: 30000,
      });
      setImageUrl(data.url);
      setUploading(false);
      return data.url;
    } catch (err) {
      setUploading(false);
      throw new Error('Image upload failed');
    }
  };

  const handleGenerate = async () => {
    if (!imageFile && !imageUrl) return alert('Please upload a keyframe image');
    if (!prompt.trim()) return alert('Please enter a prompt');

    try {
      setGenerating(true);
      setVideoUrl(null);
      try { onVideoStateChange?.(true, null, null, null); } catch {}

      const url = await uploadImage();
      if (!url) { alert('Image upload returned no URL'); setGenerating(false); return; }
      try { onVideoStateChange?.(true, null, url, prompt.trim()); } catch {}
      const { data } = await axios.post(`${API}/api/recipes/seedance-direct/generate`, {
        image_url: url,
        prompt: prompt.trim(),
        aspect_ratio: aspectRatio,
        duration,
      }, { headers: { Authorization: `Bearer ${token}` } });

      const result = await pollTask(data.task_id, token);
      if (result.video_url) {
        setVideoUrl(result.video_url);
        try { onVideoStateChange?.(false, result.video_url, null, null); } catch {}
        // Refresh runs
        axios.get(`${API}/api/recipes/seedance-direct/runs`, {
          headers: { Authorization: `Bearer ${token}` },
        }).then(({ data: d }) => setRuns(d.runs || [])).catch(() => {});
      }
    } catch (err) {
      const msg = err.message || 'Generation failed';
      alert(msg);
      try { onVideoStateChange?.(false, null, null, null); } catch {}
    } finally {
      setGenerating(false);
    }
  };

  const loadRun = (run) => {
    if (run.inputs) {
      setPrompt(run.inputs.prompt || '');
      setAspectRatio(run.inputs.aspect_ratio || '16:9');
      setDuration(run.inputs.duration || 5);
      if (run.inputs.image_url) {
        setImageUrl(run.inputs.image_url);
        setImagePreview(run.inputs.image_url);
        setImageFile(null);
      }
    }
    const videoOut = run.outputs?.find(o => o.type === 'video' && o.url);
    if (videoOut) {
      setVideoUrl(videoOut.url);
      setGenerating(false);
      onVideoStateChange?.(false, videoOut.url);
    }
  };

  const timeAgo = (iso) => {
    if (!iso) return '';
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <div className="space-y-4" data-testid="seedance-direct-inputs">
      {/* Keyframe Upload */}
      <div>
        <label className="text-[10px] text-white/40 uppercase tracking-wider block mb-1.5">Keyframe Image</label>
        <input type="file" ref={fileInputRef} accept="image/*" className="hidden" onChange={handleImageSelect} />
        {imagePreview ? (
          <div className={`relative group rounded-xl overflow-hidden ${imageOrientation === 'portrait' ? 'max-w-[60%] mx-auto' : 'w-full'}`} style={{ border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(0,0,0,0.3)' }}>
            <img src={imagePreview} alt="Keyframe" className="w-full object-contain" style={{ maxHeight: imageOrientation === 'portrait' ? '280px' : '140px' }} />
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="px-3 py-1.5 rounded-lg text-[10px] font-medium text-white bg-white/20 hover:bg-white/30 backdrop-blur-sm transition-all"
                data-testid="change-keyframe-btn"
              >
                Change
              </button>
              <button
                onClick={() => setLightbox(imagePreview)}
                className="p-1.5 rounded-lg text-white bg-white/20 hover:bg-white/30 backdrop-blur-sm transition-all"
              >
                <Maximize2 className="w-3 h-3" />
              </button>
            </div>
            {uploading && (
              <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                <Loader2 className="w-5 h-5 text-cyan-400 animate-spin" />
              </div>
            )}
          </div>
        ) : (
          <button
            onClick={() => fileInputRef.current?.click()}
            className="w-full py-8 rounded-xl flex flex-col items-center justify-center gap-2 transition-all hover:bg-white/[0.04]"
            style={{ border: '1px dashed rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.02)' }}
            data-testid="upload-keyframe-btn"
          >
            <Upload className="w-5 h-5 text-white/30" />
            <span className="text-[11px] text-white/40">Upload keyframe image</span>
            <span className="text-[9px] text-white/20">JPG, PNG, WebP</span>
          </button>
        )}
      </div>

      {/* Prompt */}
      <div>
        <label className="text-[10px] text-white/40 uppercase tracking-wider block mb-1.5">Prompt</label>
        <textarea
          value={prompt}
          onChange={e => setPrompt(e.target.value)}
          placeholder="Describe the motion and action for the video..."
          rows={4}
          className="w-full px-3 py-2.5 rounded-xl text-xs text-white/80 placeholder-white/20 bg-white/[0.03] border border-white/[0.08] focus:outline-none focus:border-cyan-500/30 resize-none transition-colors"
          data-testid="seedance-prompt-input"
        />
      </div>

      {/* Aspect Ratio & Duration */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] text-white/40 uppercase tracking-wider block mb-1.5">Aspect Ratio</label>
          <div className="flex gap-1.5">
            {['16:9', '9:16'].map(ar => (
              <button
                key={ar}
                onClick={() => setAspectRatio(ar)}
                className={`flex-1 py-2 rounded-lg text-[11px] font-medium transition-all ${aspectRatio === ar ? 'bg-cyan-500/15 border-cyan-500/40 text-cyan-300' : 'bg-white/[0.03] border-white/[0.08] text-white/50 hover:bg-white/[0.06]'}`}
                style={{ border: '1px solid' }}
                data-testid={`ar-${ar.replace(':', '-')}-btn`}
              >
                {ar === '16:9' ? '16:9 Landscape' : '9:16 Reels'}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-[10px] text-white/40 uppercase tracking-wider block mb-1.5">Duration</label>
          <div className="flex gap-1.5">
            {[5, 10, 15].map(d => (
              <button
                key={d}
                onClick={() => setDuration(d)}
                className={`flex-1 py-2 rounded-lg text-[11px] font-medium transition-all ${duration === d ? 'bg-violet-500/15 border-violet-500/40 text-violet-300' : 'bg-white/[0.03] border-white/[0.08] text-white/50 hover:bg-white/[0.06]'}`}
                style={{ border: '1px solid' }}
                data-testid={`dur-${d}s-btn`}
              >
                {d}s
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Generate Button */}
      <button
        onClick={handleGenerate}
        disabled={generating || (!imageFile && !imageUrl) || !prompt.trim()}
        className="w-full py-3 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-40 flex items-center justify-center gap-2"
        style={{
          background: generating ? 'rgba(14,165,233,0.12)' : 'linear-gradient(135deg, #0EA5E9, #0284C7)',
          boxShadow: generating ? 'none' : '0 4px 16px rgba(14,165,233,0.25)',
        }}
        data-testid="seedance-generate-btn"
      >
        {generating ? (
          <><Loader2 className="w-4 h-4 animate-spin" /> Generating {duration}s Video...</>
        ) : (
          <><Play className="w-4 h-4" /> Generate Video</>
        )}
      </button>

      {/* Recent Runs */}
      {runs.length > 0 && (
        <div data-testid="seedance-recent-runs">
          <button
            onClick={() => setRunsOpen(!runsOpen)}
            className="flex items-center justify-between w-full py-1.5 mb-1"
          >
            <div className="flex items-center gap-1.5">
              <Clock className="w-3 h-3 text-white/30" />
              <span className="text-[10px] text-white/40 uppercase tracking-wider font-semibold">Recent Runs</span>
              <span className="text-[9px] text-white/20 bg-white/[0.05] px-1.5 py-0.5 rounded-full">{runs.length}</span>
            </div>
            <ChevronDown className={`w-3 h-3 text-white/20 transition-transform ${runsOpen ? 'rotate-180' : ''}`} />
          </button>
          {runsOpen && (
            <div className="space-y-1.5 max-h-64 overflow-y-auto" data-testid="seedance-runs-list">
              {runs.map(run => {
                const videoOut = run.outputs?.find(o => o.type === 'video' && o.url);
                const isActive = videoOut && videoUrl === videoOut.url;
                return (
                  <button
                    key={run.run_id}
                    onClick={() => loadRun(run)}
                    className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-xl text-left transition-all ${isActive ? 'bg-cyan-500/10 border-cyan-500/20' : 'bg-white/[0.02] border-white/[0.06] hover:bg-white/[0.04]'}`}
                    style={{ border: '1px solid' }}
                    data-testid={`run-${run.run_id}`}
                  >
                    {/* Thumbnail */}
                    <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-black/40">
                      {run.inputs?.image_url ? (
                        <img src={run.inputs.image_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Film className="w-4 h-4 text-white/20" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] text-white/60 truncate">{run.inputs?.prompt || 'No prompt'}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className={`text-[9px] font-medium ${run.status === 'completed' ? 'text-emerald-400/70' : run.status === 'failed' ? 'text-red-400/70' : 'text-amber-400/70'}`}>
                          {run.status === 'completed' ? 'Done' : run.status === 'failed' ? 'Failed' : 'Processing'}
                        </span>
                        <span className="text-[9px] text-white/20">{run.inputs?.duration || 5}s</span>
                        <span className="text-[9px] text-white/20">{run.inputs?.aspect_ratio || '16:9'}</span>
                        <span className="text-[9px] text-white/15">{timeAgo(run.started_at)}</span>
                      </div>
                    </div>
                    {isActive && <Check className="w-3 h-3 text-cyan-400 flex-shrink-0" />}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="Keyframe" className="max-w-full max-h-full rounded-xl" />
          <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 p-2 rounded-full bg-black/60 text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
    </div>
  );
}
